import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Select, Button, Alert, Card, Progress, Modal, Form, DatePicker, InputNumber, Input, message, Drawer, Descriptions } from 'antd';
import { PlusOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getProjects, getDPRs, createDPR } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Project {
  id: string;
  code: string;
  name: string;
}

const weatherColorMap: { [key: string]: string } = {
  CLEAR: 'green',
  PARTLY_CLOUDY: 'cyan',
  OVERCAST: 'default',
  LIGHT_RAIN: 'blue',
  HEAVY_RAIN: 'geekblue',
  EXTREME: 'red',
};

const DPRPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [dprs, setDprs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDPR, setSelectedDPR] = useState<any>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        loadDPRs(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadDPRs = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getDPRs(projectId);
      setDprs(response.data);
    } catch (err) {
      setError('Failed to load DPRs.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadDPRs(value);
  };

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ report_date: dayjs() });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        report_date: values.report_date.format('YYYY-MM-DD'),
        actual_progress_pct: values.actual_progress_pct,
        planned_progress_pct: values.planned_progress_pct,
        manpower_count: values.manpower_count,
        weather: values.weather,
        key_events: values.key_events,
      };
      await createDPR(selectedProject, payload);
      message.success('DPR created successfully.');
      setModalOpen(false);
      loadDPRs(selectedProject);
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const apiMessage = err?.response?.data?.detail || err?.response?.data?.report_date?.[0] || 'Failed to create DPR.';
      message.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const openDrawer = (record: any) => {
    setSelectedDPR(record);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedDPR(null);
  };

  const columns = [
    {
      title: 'Report Date',
      dataIndex: 'report_date',
      key: 'report_date',
      render: (date: string) => (
        <Text strong style={{ color: '#1B2A4A' }}>
          {new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </Text>
      ),
    },
    {
      title: 'Progress',
      dataIndex: 'actual_progress_pct',
      key: 'actual_progress_pct',
      render: (pct: string) => (
        <div style={{ width: 150 }}>
          <Progress
            percent={parseFloat(pct) || 0}
            size="small"
            strokeColor="#2E75B6"
            format={p => `${p}%`}
          />
        </div>
      ),
    },
    {
      title: 'Manpower',
      dataIndex: 'manpower_count',
      key: 'manpower_count',
      render: (count: number) => count || '—',
    },
    {
      title: 'Weather',
      dataIndex: 'weather',
      key: 'weather',
      render: (weather: string) => weather ? <Tag color={weatherColorMap[weather] || 'default'}>{weather.replace('_', ' ')}</Tag> : '—',
    },
    {
      title: 'Status',
      dataIndex: 'is_locked',
      key: 'is_locked',
      render: (locked: boolean) => locked
        ? <Tag icon={<CheckCircleOutlined />} color="green">Locked</Tag>
        : <Tag icon={<CloseCircleOutlined />} color="orange">Open</Tag>,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          DPR Register
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={openModal} disabled={!selectedProject}>
            Create DPR
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={dprs}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No DPRs found for this project.' }}
          onRow={(record) => ({
            onClick: () => openDrawer(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Drawer
        title={selectedDPR ? new Date(selectedDPR.report_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'DPR Details'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={480}
      >
        {selectedDPR && (
          <>
            <div style={{ marginBottom: 16 }}>
              {selectedDPR.weather && (
                <Tag color={weatherColorMap[selectedDPR.weather] || 'default'}>
                  {selectedDPR.weather.replace('_', ' ')}
                </Tag>
              )}
              {selectedDPR.is_locked
                ? <Tag icon={<CheckCircleOutlined />} color="green">Locked</Tag>
                : <Tag icon={<CloseCircleOutlined />} color="orange">Open</Tag>}
            </div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Planned Progress">
                {selectedDPR.planned_progress_pct != null ? `${selectedDPR.planned_progress_pct}%` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Actual Progress">
                {selectedDPR.actual_progress_pct != null ? `${selectedDPR.actual_progress_pct}%` : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Manpower Count">{selectedDPR.manpower_count ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Key Events">{selectedDPR.key_events || '—'}</Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Drawer>

      <Modal
        title="Create Daily Progress Report"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Submit DPR"
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="report_date"
            label="Report Date"
            rules={[{ required: true, message: 'Report date is required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD-MMM-YYYY" />
          </Form.Item>

          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item
              name="planned_progress_pct"
              label="Planned Progress (%)"
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="actual_progress_pct"
              label="Actual Progress (%)"
              rules={[{ required: true, message: 'Actual progress is required' }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item
            name="manpower_count"
            label="Manpower Count"
            rules={[{ required: true, message: 'Manpower count is required' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="weather"
            label="Weather"
            rules={[{ required: true, message: 'Weather is required' }]}
          >
            <Select
              options={[
                { value: 'CLEAR', label: 'Clear' },
                { value: 'PARTLY_CLOUDY', label: 'Partly Cloudy' },
                { value: 'OVERCAST', label: 'Overcast' },
                { value: 'LIGHT_RAIN', label: 'Light Rain' },
                { value: 'HEAVY_RAIN', label: 'Heavy Rain' },
                { value: 'EXTREME', label: 'Extreme' },
              ]}
            />
          </Form.Item>

          <Form.Item name="key_events" label="Key Events">
            <TextArea rows={3} placeholder="Brief note on key events of the day" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DPRPage;